import usePersonalPresetsEnabled from '@src/hooks/usePersonalPresetsEnabled';
import {
  dimensionRowLimitSelector,
  realTimeModeOnTrendsSelector,
  currentMetricsMetaSelector,
  getFirstAppliedFilter,
} from '@store/common/selectors';
import { useSelector } from 'react-redux';
import useFetchGroupBy from './useFetchGroupBy';
import {
  formatColumns,
  formatData,
  formatMultiColumns,
  formatMultiData,
} from '../dimension-table/utils';
import {
  chartIntervalSelector,
  noBrushSelector,
  tableCompareIntervalSelector,
} from '@store/trends/birdChart/selectors';
import { dimensionMetricsSelector, metricFilterRulesSelector } from '@store/trends/base/selectors';
import { selectedMetricSelector } from '@store/metricsChartContainer/selectors';
import { useEffect, useState, useMemo } from 'react';
import { getMetricByUnit } from '@utils/metrics/metricHelpers';
import useSetDimensionExport from './useSetDimensionExport';
import { tableMetricsAtom, isTableCompareEnabledAtom, modeAtom, TABLE_MODES } from '../atoms';
import { useAtomValue } from 'jotai';
import { dimensionByTypesSelector } from '@store/common/dimensionList/selectors';
import { showNAForDimensionMetric } from './utils';
import { isMetricTabActiveAtom, errorDataAtom } from '@src/components/metrics-panel-hz/atoms';
import useSort from './useSort';
import { isRateMetric } from '@utils/metrics/rateMetrics';

function useDimension({ dimension, dimensionMetricsToNA, unit, enabled = true }) {
  const personalPresets = usePersonalPresetsEnabled();
  const currentMetricsMeta = useSelector(currentMetricsMetaSelector);
  const filter = useSelector(getFirstAppliedFilter);
  const interval: any = useSelector(chartIntervalSelector);
  const metricFilter = useSelector(metricFilterRulesSelector);
  const selectedMetric = useSelector(selectedMetricSelector);
  const realTimeOnTrends: boolean = useSelector(realTimeModeOnTrendsSelector);
  const metrics = useSelector(dimensionMetricsSelector) as string[];
  const dimensionsByTypes = useSelector(dimensionByTypesSelector);
  const isTableCompareEnabled = useAtomValue(isTableCompareEnabledAtom);
  const comparisonInterval: any = useSelector(tableCompareIntervalSelector);
  const isMetricTabActive = useAtomValue(isMetricTabActiveAtom);
  const mode = useAtomValue(modeAtom);
  const showActionColumn = !realTimeOnTrends && (isMetricTabActive || mode === TABLE_MODES.filter);

  const showNAForMetric = showNAForDimensionMetric(
    filter,
    dimensionsByTypes,
    dimensionMetricsToNA,
    dimension,
  );
  const { metrics: queryMetrics, orderBy: sortByMetric }: any = useAtomValue(tableMetricsAtom);
  const noBrushSelected: boolean = useSelector(noBrushSelector);
  const { limit } = useSelector(dimensionRowLimitSelector);
  const errorData = useAtomValue(errorDataAtom);
  const columnMetrics = useMemo(
    () => metrics.map((metric) => getMetricByUnit(unit, metric)),
    [metrics, unit],
  );
  const { sortBy, onColumnSort } = useSort({
    sortByMetric,
    dimension,
    columnMetrics: [...columnMetrics, ...metrics.filter(isRateMetric)],
  });

  const [searchText, onSearch] = useState('');

  // update local sort when selected or order by metric changes
  useEffect(() => {
    if (!personalPresets) onColumnSort({ key: sortByMetric, order: 'desc' });
  }, [sortByMetric, selectedMetric]);

  const {
    isFetching,
    isError,
    data = {},
    refetch,
  } = useFetchGroupBy({
    dimension,
    metrics: queryMetrics,
    filter,
    interval,
    enabled,
    metricFilter,
    sortByMetric,
    limit,
    realTimeOnTrends,
    noBrushSelected,
    comparisonInterval,
    isTableCompareEnabled,
  });

  const { adjustedInterval }: any = data;

  const { rows, totals, columns, pctShareMaxValues } = useMemo(() => {
    let rows = [];
    let totals = {};
    let columns = [];
    let pctShareMaxValues = {};

    // Rate columns keep their identity, but count-mode sorting uses count values.
    const dataSortBy = isRateMetric(sortBy.key)
      ? { ...sortBy, key: getMetricByUnit(unit, sortBy.key) }
      : sortBy;

    // TODO: improve formatting
    if ('metricFilterData' in data) {
      ({ rows, totals, pctShareMaxValues } = formatMultiData({
        data,
        sortBy: dataSortBy,
        searchText,
        metrics: columnMetrics,
      }));
      columns = formatMultiColumns({
        labelMetrics: metrics,
        dimension,
        metrics: columnMetrics,
        showNAForMetric,
        showActionColumn,
        showTrend: isTableCompareEnabled,
        errorData,
        flowId: null,
      });
    } else {
      ({ rows, totals, pctShareMaxValues } = formatData({
        data,
        sortBy: dataSortBy,
        searchText,
      }));
      columns = formatColumns({
        labelMetrics: metrics,
        dimension,
        metrics: columnMetrics,
        showNAForMetric,
        showActionColumn,
        showTrend: isTableCompareEnabled,
        errorData,
        disableDrilldown: false,
        showHeaderTooltip: false,
      });
    }

    return { rows, totals, columns, pctShareMaxValues };
  }, [
    data,
    dimension,
    metrics,
    currentMetricsMeta,
    columnMetrics,
    unit,
    showActionColumn,
    searchText,
    showNAForMetric,
    sortBy,
    unit,
    errorData,
    isTableCompareEnabled,
  ]);

  useSetDimensionExport({
    dimension,
    metrics: metrics.filter((m) => !errorData[m]),
    data: rows,
    totals,
    showNAForMetric,
  });

  return {
    isFetching,
    isError,
    refetch,
    columns,
    isEmpty: rows.length === 0,
    totals,
    data: rows,
    sortBy,
    onColumnSort,
    onSearch,
    searchText,
    adjustedInterval,
    pctShareMaxValues,
    metrics,
  };
}

export default useDimension;
